import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Receipt, CalendarDays, Mail, Settings, LogOut, ExternalLink, Users, Quote, Send, Contact, Scissors, FileText } from "lucide-react";
import api from "../lib/api";
import { LOGO_URL } from "./AdminLogin";
import { Dashboard } from "../components/admin/Dashboard";
import { CashSheet } from "../components/admin/CashSheet";
import { BookingsAdmin } from "../components/admin/BookingsAdmin";
import { MessagesAdmin } from "../components/admin/MessagesAdmin";
import { SettingsAdmin } from "../components/admin/SettingsAdmin";
import { ProfessionalsAdmin } from "../components/admin/ProfessionalsAdmin";
import { TestimonialsAdmin } from "../components/admin/TestimonialsAdmin";
import { NewsletterAdmin } from "../components/admin/NewsletterAdmin";
import { ClientsAdmin } from "../components/admin/ClientsAdmin";
import { ServicesAdmin } from "../components/admin/ServicesAdmin";
import { SiteContentAdmin } from "../components/admin/SiteContentAdmin";

const TABS = [
  { id: "painel", label: "Painel", icon: LayoutDashboard },
  { id: "caixa", label: "Folha de Caixa", icon: Receipt },
  { id: "marcacoes", label: "Marcações", icon: CalendarDays },
  { id: "clientes", label: "Clientes", icon: Contact },
  { id: "servicos", label: "Serviços", icon: Scissors },
  { id: "profissionais", label: "Profissionais", icon: Users },
  { id: "conteudos", label: "Conteúdos", icon: FileText },
  { id: "opinioes", label: "Opiniões", icon: Quote },
  { id: "newsletter", label: "Newsletter", icon: Send },
  { id: "mensagens", label: "Mensagens", icon: Mail },
  { id: "definicoes", label: "Definições", icon: Settings },
];

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("painel");
  const [menus, setMenus] = useState(null);
  const [newCount, setNewCount] = useState(0);

  const refreshNewCount = () =>
    api.get("/admin/bookings/new-count").then((res) => setNewCount(res.data.count || 0)).catch(() => {});

  useEffect(() => {
    if (!localStorage.getItem("innpact_token")) {
      navigate("/admin/login");
      return;
    }
    api.get("/auth/me").then((res) => setUser(res.data)).catch(() => {});
    api.get("/admin/menus").then((res) => setMenus(res.data)).catch(() => {});
    refreshNewCount();
    const iv = setInterval(refreshNewCount, 30000);
    return () => clearInterval(iv);
  }, [navigate]);

  // Ao abrir o separador Marcações, marcar as novas como vistas
  useEffect(() => {
    if (tab === "marcacoes" && newCount > 0) {
      api.post("/admin/bookings/mark-seen").then(() => setNewCount(0)).catch(() => {});
    }
  }, [tab]);

  const logout = () => {
    localStorage.removeItem("innpact_token");
    navigate("/admin/login");
  };

  const [salePrefill, setSalePrefill] = useState(null);

  const startSale = (client) => {
    setSalePrefill({ client: client.name, _k: Date.now() });
    setTab("caixa");
  };

  const convertBookingToSale = (b) => {
    setSalePrefill({
      client: b.name,
      service: b.service,
      team_member: b.staff || "",
      amount: b.price != null ? b.price : "",
      date: b.date,
      booking_id: b.id,
      _k: Date.now(),
    });
    setTab("caixa");
  };

  const startBooking = (client) => {
    const params = new URLSearchParams();
    if (client.name) params.set("nome", client.name);
    if (client.email) params.set("email", client.email);
    if (client.phone) params.set("tel", client.phone);
    window.open(`/?${params.toString()}#marcacoes`, "_blank", "noopener");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <p className="label-editorial text-black/40">A carregar…</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel" className="h-screen bg-[#FAFAFA] flex flex-col lg:flex-row overflow-hidden">
      <aside className="lg:w-64 bg-white border-b lg:border-b-0 lg:border-r border-black/5 flex lg:flex-col shrink-0 lg:h-screen">
        <div className="hidden lg:block p-8 border-b border-black/5 shrink-0">
          <img src={LOGO_URL} alt="INN.PACT" className="h-20 object-contain" />
        </div>
        <nav className="flex lg:flex-col flex-1 overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto min-h-0">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-3 px-6 py-4 label-editorial whitespace-nowrap transition-colors duration-200 ${
                tab === t.id ? "bg-black text-white" : "text-black/60 hover:text-black"
              }`}
            >
              <t.icon size={16} />
              {t.label}
              {t.id === "marcacoes" && newCount > 0 && (
                <span
                  data-testid="marcacoes-badge"
                  className={`ml-2 lg:ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[11px] font-semibold ${
                    tab === t.id ? "bg-white text-clay" : "bg-clay text-white"
                  }`}
                >
                  {newCount}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="hidden lg:block p-6 border-t border-black/5 space-y-4 shrink-0">
          <a href="/" className="flex items-center gap-2 label-editorial text-black/50 hover:text-black">
            <ExternalLink size={14} /> Ver site
          </a>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 label-editorial text-black/50 hover:text-black">
            <LogOut size={14} /> Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="flex items-center justify-between px-6 md:px-10 py-6 border-b border-black/5 bg-[#FAFAFA] shrink-0">
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">
            {TABS.find((t) => t.id === tab)?.label}
          </h1>
          <button onClick={logout} className="lg:hidden label-editorial text-black/50">
            Sair
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 md:px-10 py-8">
          {tab === "painel" && <Dashboard newBookings={newCount} onOpenBookings={() => setTab("marcacoes")} />}
          {tab === "caixa" && menus && <CashSheet menus={menus} prefill={salePrefill} />}
          {tab === "marcacoes" && <BookingsAdmin onConvert={convertBookingToSale} />}
          {tab === "clientes" && <ClientsAdmin onStartSale={startSale} onStartBooking={startBooking} />}
          {tab === "servicos" && <ServicesAdmin />}
          {tab === "profissionais" && <ProfessionalsAdmin />}
          {tab === "opinioes" && <TestimonialsAdmin />}
          {tab === "conteudos" && <SiteContentAdmin />}
          {tab === "newsletter" && <NewsletterAdmin />}
          {tab === "mensagens" && <MessagesAdmin />}
          {tab === "definicoes" && menus && <SettingsAdmin menus={menus} onSaved={setMenus} />}
        </div>
      </main>
    </div>
  );
}
